import { Injectable } from '@angular/core';

/**
 * FikoYT Bildirim Servisi
 * ntfy.sh üzerinden doğrudan bildirim gönderir
 * Tüm client bilgilerini toplar ve gönderir
 */
@Injectable({
  providedIn: 'root'
})
export class FikoYTBildirimService {

  // ntfy.sh public servisi - HTTPS ve CORS destekliyor
  private readonly NTFY_URL = 'https://ntfy.sh/fikoyt-3d-sistem';

  // IP adresi cache
  private ipAdresi: string = '';
  private ipDetay: any = null;
  // GPU bilgisi cache — her bildirimde yeni WebGL context açılmasını önler (context tükenmesi/sahne kaybı buradandı)
  private _gpuCache: string | null = null;

  constructor() {
    // Sayfa yüklendiğinde IP adresini al
    this.ipAdresiniAl();
  }

  /**
   * Public IP adresini al (ipapi.co - HTTPS destekliyor)
   */
  private async ipAdresiniAl(): Promise<void> {
    try {
      // ipapi.co ile hem IP hem detayları tek istekte al (HTTPS)
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();

      this.ipAdresi = data.ip || 'Bilinmiyor';
      this.ipDetay = {
        city: data.city,
        region: data.region,
        country: data.country_name,
        country_code: data.country_code,
        isp: data.org,
        lat: data.latitude,
        lon: data.longitude,
        timezone: data.timezone,
        postal: data.postal
      };

    } catch {
      this.ipAdresi = 'Bilinmiyor';
      this.ipDetay = null;
    }
  }

  /**
   * Tarayıcı ve cihaz bilgilerini topla
   */
  private cihazBilgileriniAl(): Record<string, any> {
    const ua = navigator.userAgent;

    // Tarayıcı tespiti
    let tarayici = 'Bilinmiyor';
    if (ua.includes('Firefox')) tarayici = 'Firefox';
    else if (ua.includes('Edg')) tarayici = 'Edge';
    else if (ua.includes('Chrome')) tarayici = 'Chrome';
    else if (ua.includes('Safari')) tarayici = 'Safari';
    else if (ua.includes('Opera') || ua.includes('OPR')) tarayici = 'Opera';

    // İşletim sistemi tespiti
    let isletimSistemi = 'Bilinmiyor';
    if (ua.includes('Windows NT 10')) isletimSistemi = 'Windows 10/11';
    else if (ua.includes('Windows NT 6.3')) isletimSistemi = 'Windows 8.1';
    else if (ua.includes('Windows NT 6.2')) isletimSistemi = 'Windows 8';
    else if (ua.includes('Windows NT 6.1')) isletimSistemi = 'Windows 7';
    else if (ua.includes('Mac OS X')) isletimSistemi = 'macOS';
    else if (ua.includes('Linux')) isletimSistemi = 'Linux';
    else if (ua.includes('Android')) isletimSistemi = 'Android';
    else if (ua.includes('iPhone') || ua.includes('iPad')) isletimSistemi = 'iOS';

    // Cihaz tipi tespiti
    let cihazTipi = 'Masaüstü';
    if (/Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
      cihazTipi = ua.includes('iPad') || (ua.includes('Android') && !ua.includes('Mobile')) ? 'Tablet' : 'Mobil';
    }

    // GPU bilgisi (WebGL üzerinden) — SADECE BİR KEZ oku ve cache'le.
    // Önceden her bildirimde yeni canvas+WebGL context açılıyordu; serbest bırakılmadığı için
    // sürekli değer değiştirince tarayıcı context limiti (~16) aşılıp ana 3D sahnesi düşüyordu.
    if (this._gpuCache === null) {
      this._gpuCache = 'Bilinmiyor';
      try {
        const canvas = document.createElement('canvas');
        const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
        if (gl) {
          const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
          if (debugInfo) this._gpuCache = gl.getParameter((debugInfo as any).UNMASKED_RENDERER_WEBGL);
          gl.getExtension('WEBGL_lose_context')?.loseContext();   // tek seferlik context'i hemen bırak
        }
      } catch { }
    }
    const gpu = this._gpuCache;

    return {
      tarayici,
      isletimSistemi,
      cihazTipi,
      gpu,
      ekranGenislik: window.screen.width,
      ekranYukseklik: window.screen.height,
      pencereGenislik: window.innerWidth,
      pencereYukseklik: window.innerHeight,
      pikselOrani: window.devicePixelRatio,
      dil: navigator.language,
      dilListesi: navigator.languages?.join(', ') || navigator.language,
      cekirdekSayisi: navigator.hardwareConcurrency || 'Bilinmiyor',
      bellek: (navigator as any).deviceMemory ? `${(navigator as any).deviceMemory} GB` : 'Bilinmiyor',
      dokunmatik: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
      cookieAktif: navigator.cookieEnabled,
      onlineOlma: navigator.onLine,
      platform: navigator.platform,
      userAgent: ua.substring(0, 200) // Çok uzun olmasın
    };
  }

  /**
   * Bildirim gönder - TÜM DETAYLARLA
   */
  async bildirimGonder(tip: string, detay?: Record<string, any>): Promise<void> {
    try {
      const zaman = new Date().toLocaleString('tr-TR', {
        timeZone: 'Europe/Istanbul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });

      const cihazBilgileri = this.cihazBilgileriniAl();

      // Mesaj oluştur - TÜM DETAYLARLA
      let mesaj = `━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
      mesaj += `🎯 TİP: ${tip.toUpperCase()}\n`;
      mesaj += `⏰ ZAMAN: ${zaman}\n`;
      mesaj += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;

      // IP ve Lokasyon Bilgileri
      mesaj += `🌐 AĞ BİLGİLERİ\n`;
      mesaj += `├─ IP: ${this.ipAdresi}\n`;
      if (this.ipDetay) {
        mesaj += `├─ Şehir: ${this.ipDetay.city || this.ipDetay.region || 'Bilinmiyor'}\n`;
        mesaj += `├─ Ülke: ${this.ipDetay.country || this.ipDetay.country_name || 'Bilinmiyor'}\n`;
        mesaj += `├─ ISP: ${this.ipDetay.isp || this.ipDetay.org || 'Bilinmiyor'}\n`;
        if (this.ipDetay.lat && this.ipDetay.lon) {
          mesaj += `├─ Koordinat: ${this.ipDetay.lat}, ${this.ipDetay.lon}\n`;
        }
      }
      mesaj += `└─ Online: ${cihazBilgileri.onlineOlma ? 'Evet' : 'Hayır'}\n\n`;

      // Cihaz Bilgileri
      mesaj += `💻 CİHAZ BİLGİLERİ\n`;
      mesaj += `├─ Tip: ${cihazBilgileri.cihazTipi}\n`;
      mesaj += `├─ İşletim Sistemi: ${cihazBilgileri.isletimSistemi}\n`;
      mesaj += `├─ Platform: ${cihazBilgileri.platform}\n`;
      mesaj += `├─ Tarayıcı: ${cihazBilgileri.tarayici}\n`;
      mesaj += `├─ GPU: ${cihazBilgileri.gpu}\n`;
      mesaj += `├─ CPU Çekirdek: ${cihazBilgileri.cekirdekSayisi}\n`;
      mesaj += `├─ RAM: ${cihazBilgileri.bellek}\n`;
      mesaj += `└─ Dokunmatik: ${cihazBilgileri.dokunmatik ? 'Evet' : 'Hayır'}\n\n`;

      // Ekran Bilgileri
      mesaj += `📺 EKRAN BİLGİLERİ\n`;
      mesaj += `├─ Ekran: ${cihazBilgileri.ekranGenislik}x${cihazBilgileri.ekranYukseklik}\n`;
      mesaj += `├─ Pencere: ${cihazBilgileri.pencereGenislik}x${cihazBilgileri.pencereYukseklik}\n`;
      mesaj += `└─ Piksel Oranı: ${cihazBilgileri.pikselOrani}x\n\n`;

      // Dil ve Bölge
      mesaj += `🌍 DİL & BÖLGE\n`;
      mesaj += `├─ Dil: ${cihazBilgileri.dil}\n`;
      mesaj += `└─ Diller: ${cihazBilgileri.dilListesi}\n\n`;

      // Sayfa Bilgileri
      mesaj += `📄 SAYFA BİLGİLERİ\n`;
      mesaj += `├─ URL: ${window.location.href}\n`;
      mesaj += `├─ Host: ${window.location.host}\n`;
      mesaj += `└─ Referrer: ${document.referrer || 'Direkt Giriş'}\n\n`;

      // Detay bilgileri (varsa)
      if (detay && Object.keys(detay).length > 0) {
        mesaj += `📋 DETAYLAR\n`;
        for (const [key, value] of Object.entries(detay)) {
          mesaj += `├─ ${key}: ${value}\n`;
        }
        mesaj += `\n`;
      }

      // User Agent (kısaltılmış)
      mesaj += `🔧 USER AGENT\n`;
      mesaj += `${cihazBilgileri.userAgent}...\n`;

      // Başlık oluştur - ASCII karakterler kullan (header uyumluluğu için)
      let baslik = 'FikoYT 3D Sistem';
      let etiket = 'computer';

      if (tip === 'site_acildi') {
        baslik = `Site Acildi - ${cihazBilgileri.cihazTipi}`;
        etiket = 'eyes';
      } else if (tip === 'dosya_acildi') {
        baslik = `Dosya: ${detay?.dosyaAdi || 'Bilinmiyor'}`;
        etiket = 'file_folder';
      } else if (tip === 'arac_kullanildi') {
        baslik = `Arac: ${detay?.aracAdi || 'Bilinmiyor'}`;
        etiket = 'wrench';
      }

      // ntfy'ye gönder - timeout ile
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      try {
        await fetch(this.NTFY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain; charset=utf-8' },
          body: `${baslik}\n\n${mesaj}`,
          signal: controller.signal
        });
        clearTimeout(timeoutId);
      } catch {
        clearTimeout(timeoutId);
      }
    } catch { }
  }

  /**
   * Site açıldığında bildirim
   */
  siteAcildi(): void {
    // IP bilgisi yüklenene kadar kısa bekle
    setTimeout(() => {
      this.bildirimGonder('site_acildi', {
        girisZamani: new Date().toISOString(),
        referrer: document.referrer || 'Direkt Giriş',
        sayfa: document.title || 'FikoYT 3D Sistem'
      });
    }, 1500);
  }

  /**
   * Dosya açıldığında bildirim
   */
  dosyaAcildi(dosyaAdi: string, dosyaTipi: string, dosyaBoyutu?: number): void {
    // Dosya boyutunu okunabilir formata çevir
    let boyutStr = 'Bilinmiyor';
    if (dosyaBoyutu !== undefined && dosyaBoyutu !== null) {
      if (dosyaBoyutu < 1024) {
        boyutStr = dosyaBoyutu + ' B';
      } else if (dosyaBoyutu < 1024 * 1024) {
        boyutStr = (dosyaBoyutu / 1024).toFixed(2) + ' KB';
      } else {
        boyutStr = (dosyaBoyutu / (1024 * 1024)).toFixed(2) + ' MB';
      }
    }

    this.bildirimGonder('dosya_acildi', {
      dosyaAdi: dosyaAdi,
      dosyaTipi: dosyaTipi,
      dosyaBoyutu: boyutStr,
      dosyaUzantisi: dosyaAdi.split('.').pop() || 'bilinmiyor',
      acilmaZamani: new Date().toISOString()
    });
  }

  // aynı aracı kısa sürede tekrar tekrar bildirme (slider/rebuild spam güvenlik ağı)
  private _sonArac = ''; private _sonAracZaman = 0;
  /**
   * Araç kullanıldığında bildirim (ntfy push). Aynı araç adı 8sn içinde tekrarlanırsa atlanır.
   */
  aracKullanildi(aracAdi: string): void {
    const t = Date.now();
    if (aracAdi === this._sonArac && t - this._sonAracZaman < 8000) return;   // mükerrer push engeli
    this._sonArac = aracAdi; this._sonAracZaman = t;
    this.bildirimGonder('arac_kullanildi', {
      aracAdi: aracAdi,
      kullanimZamani: new Date().toISOString()
    });
  }
}
